import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

export const mergeMessages = (previous, incoming) => {
  const byId = new Map(previous.map(message => [message.id, message]));
  incoming.forEach(message => byId.set(message.id, message));
  return [...byId.values()].sort((a, b) => a.id - b.id);
};

export default function useConversation({ socket, userId, partnerId, token }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const generation = useRef(0);
  const busy = useRef(false);
  const headers = useCallback(() => ({ Authorization: `Bearer ${token || localStorage.getItem('token')}` }), [token]);
  const load = useCallback(async () => {
    const attempt = generation.current;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/get_messages?partner_id=${partnerId}`, { headers: headers() });
      if (attempt === generation.current) {
        setMessages(previous => mergeMessages(previous, data.messages || []));
        setError('');
      }
    } catch (err) {
      if (attempt === generation.current) setError("לא ניתן לטעון הודעות. בדקו את החיבור ונסו שוב.");
    }
  }, [partnerId, headers]);
  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setSending(false);
    setMessages([]);
    setDraft('');
    setError('');
    load();
    const receive = message => {
      if ((message.sender_id === userId && message.receiver_id === partnerId) ||
          (message.sender_id === partnerId && message.receiver_id === userId)) {
        setMessages(previous => mergeMessages(previous, [message]));
      }
    };
    socket?.on('new_message', receive);
    socket?.on('connect', load);
    return () => {
      generation.current += 1;
      socket?.off('new_message', receive);
      socket?.off('connect', load);
    };
  }, [socket, userId, partnerId, load]);
  const send = async () => {
    const text = draft.trim();
    if (!text || busy.current) return;
    const attempt = generation.current;
    busy.current = true;
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/send_message`, { text, receiver_id: partnerId }, { headers: headers() });
      if (attempt === generation.current) {
        setMessages(previous => mergeMessages(previous, [data.item]));
        setDraft(value => value === draft ? '' : value);
      }
    } catch (err) {
      if (attempt === generation.current) setError(err.response?.data?.message || "שליחת ההודעה לא אושרה. הטיוטה נשמרה כאן; בדקו את השיחה לפני שתנסו שוב.");
    } finally {
      if (attempt === generation.current) { busy.current = false; setSending(false); }
    }
  };
  return { messages, draft, setDraft, error, sending, send, reload: load };
}
